import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { Input } from "../components/ui/input";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset your password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="reset-password-page">
      <span className="eyebrow">Researcher account</span>
      <h1 className="font-display font-extrabold text-4xl mt-3">Set a new password</h1>
      {!token ? (
        <div className="card-soft rounded-2xl p-6 sm:p-8 mt-8" data-testid="reset-no-token">
          <p className="text-destructive text-sm">This reset link is missing its token. Please request a new link.</p>
          <Link to="/forgot-password" className="font-semibold mt-4 inline-block underline underline-offset-4" style={{ color: "var(--gold)" }} data-testid="reset-request-new">Request a new link</Link>
        </div>
      ) : done ? (
        <div className="card-soft rounded-2xl p-6 sm:p-8 mt-8 text-center" data-testid="reset-success">
          <CheckCircle2 className="w-10 h-10 mx-auto text-[#2f7a3f]" />
          <p className="font-display font-bold text-lg mt-4">Password updated</p>
          <p className="text-muted-foreground mt-2 text-sm">Taking you to the sign-in page…</p>
        </div>
      ) : (
        <>
          <p className="text-muted-foreground mt-3">Choose a new password for your account. Minimum 8 characters.</p>
          <form onSubmit={submit} className="card-soft rounded-2xl p-6 sm:p-8 mt-8 space-y-5" data-testid="reset-password-form">
            <div>
              <label className="text-sm font-bold" htmlFor="reset-password">New password</label>
              <Input id="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-11 bg-white" data-testid="reset-password-input" />
            </div>
            <div>
              <label className="text-sm font-bold" htmlFor="reset-confirm">Confirm password</label>
              <Input id="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 h-11 bg-white" data-testid="reset-confirm-input" />
            </div>
            {error && <p role="alert" className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={busy} className="w-full h-12 rounded-xl btn-ink font-display font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reset-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Reset password
            </button>
          </form>
        </>
      )}
    </div>
  );
}
